import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * E8C · One-time in-app marketing-consent prompt.
 *
 * Mounted globally inside `CollectorAuthProvider`. On every collector
 * session it asks the backend whether the prompt should show
 * (`needs_prompt` is true ONLY when `marketing_consent_email` is
 * null/missing). Explicit True and explicit False both suppress this
 * modal forever — a "No thanks" choice is a valid preference and must
 * be respected.
 *
 * Once the user picks a choice, the answer is persisted with
 * `source="collector_prompt"` and the modal never re-renders.
 */
export default function MarketingConsentPrompt() {
  const { collector, refresh } = useCollectorAuth();
  const [needsPrompt, setNeedsPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = useCallback(async () => {
    if (!collector?.id) { setNeedsPrompt(false); return; }
    try {
      const r = await axios.get(
        `${API}/collector-auth/me/marketing-consent-status`,
        { withCredentials: true });
      setNeedsPrompt(!!r.data?.needs_prompt);
    } catch {
      setNeedsPrompt(false);
    }
  }, [collector?.id]);

  useEffect(() => { load(); }, [load]);

  const submit = useCallback(async (consent) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/collector-auth/me/marketing-consent`,
        { consent, source: "collector_prompt" },
        { withCredentials: true },
      );
      setDismissed(true);
      setNeedsPrompt(false);
      if (consent) {
        toast.success("Preference saved. We'll send helpful updates.");
      } else {
        toast.success("Preference saved.");
      }
      // Refresh the collector object so downstream consumers see the change.
      refresh && refresh();
    } catch {
      toast.error("Couldn't save your preference. Please try again.");
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  if (!collector?.id || !needsPrompt || dismissed) return null;

  return (
    <div
      className="fixed inset-0 z-[85] bg-black/50 flex items-end sm:items-center justify-center p-4"
      data-testid="marketing-consent-prompt"
    >
      <div className="bg-white border-4 border-[var(--ink)] rounded-t-2xl sm:rounded-2xl w-full max-w-md p-5">
        <div className="text-[10px] uppercase tracking-widest text-[var(--panini-red)] font-black mb-1">
          Email Preferences
        </div>
        <h2 className="text-xl md:text-2xl font-black leading-tight" data-testid="marketing-consent-prompt-title">
          Get helpful CollectorUp updates
        </h2>
        <p className="text-sm text-[var(--smoke)] mt-2 leading-relaxed">
          Get occasional reminders about your albums, missing stickers,
          duplicates and collecting progress. You can change this any
          time in your profile settings.
        </p>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={busy}
            className="px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest bg-white text-[var(--ink)] border-2 border-[var(--ink)] hover:bg-neutral-100 disabled:opacity-50"
            data-testid="marketing-consent-decline"
          >
            No thanks
          </button>
          <button
            type="button"
            onClick={() => submit(true)}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest bg-[var(--panini-red)] text-white border-2 border-[var(--ink)] hover:brightness-110 disabled:opacity-50"
            data-testid="marketing-consent-accept"
          >
            {busy ? "Saving…" : "Email me updates"}
          </button>
        </div>
      </div>
    </div>
  );
}
